/**
 * Shell Layout Component for TECDocChart Root-Level Application
 * 
 * Provides the main layout structure for the healthcare application
 * with tenant context, employee information, and navigation.
 * 
 * Issue #68 - Root-Level React Router Implementation
 */

import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { type TenantConfig } from '../../config/tenant';
import { TecSprite } from '../shared/TecSprite';
import { TopBarThrobber } from '../shared/LoadingThrobber';
import { useLoading } from '../../contexts/LoadingContext';
import { useAppDispatch } from '../../store/hooks';
import { fetchFacilities } from '../../store/facilitiesSlice';
import { useSessionKeepalive } from '../../hooks/useSessionKeepalive';
import {
  LOGOUT_WARNING_PREF_EVENT,
  readInitialDisableWarning,
  toDisableWarning,
  type LogoutWarningPrefDetail,
} from '../../utils/logoutWarning';
import ErrorBoundary from '../shared/ErrorBoundary';
import Loading from '../shared/Loading';
import MainNavbar from '../navigation/MainNavbar';
import FeedbackModal from '../feedback/FeedbackModal';
import SlashPhrasesModal from '../slashphrases/SlashPhrasesModal';
import FacilityLoginSelector from '../facility/FacilityLoginSelector';
import TermsOfUseModal from '../terms/TermsOfUseModal';
import './ShellLayout.css';

interface GlobalContext {
  loggedInEmployee?: any;
  tenantContext?: {
    schema: string;
    name: string;
    facilityId?: string;
    domain: string;
  };
  authContext?: {
    authenticated: boolean;
    user: any;
    employee: any;
    permissions: string[];
    csrf_token?: string;
  };
  appContext?: {
    version: string;
    environment: string;
    baseUrl: string;
    logoSmall?: string;
    singleUser?: boolean;
    feedbackEmail?: string;
  };
}

function ShellLayout() {
  const dispatch = useAppDispatch();
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);
  const [globalContext, setGlobalContext] = useState<GlobalContext | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showSlashPhrasesModal, setShowSlashPhrasesModal] = useState(false);
  const [showFacilitySelector, setShowFacilitySelector] = useState(false);
  const [showTermsOfUseModal, setShowTermsOfUseModal] = useState(false);
  const [termsOfUseMarkdown, setTermsOfUseMarkdown] = useState('');
  const [termsOfUseLoading, setTermsOfUseLoading] = useState(false);
  const [termsOfUseLoadError, setTermsOfUseLoadError] = useState<string | null>(null);
  // Seeded from the server-rendered employee payload so the very first tick already honours the
  // saved preference; updated in place when the account page saves a new one (issue #201).
  const [disableLogoutWarning, setDisableLogoutWarning] = useState(readInitialDisableWarning);

  const location = useLocation();
  const { loading } = useLoading();

  // Session keepalive - only enabled when not on login page
  const isLoginPage = location.pathname === '/login' || location.pathname === '/auth/login';
  useSessionKeepalive({
    enabled: !isLoginPage,
    disableWarning: disableLogoutWarning,
  });

  useEffect(() => {
    // Initialize tenant configuration and global context
    initializeConfiguration();
  }, []);

  useEffect(() => {
    const handleLogoutWarningPrefChange = (event: Event) => {
      const detail = (event as CustomEvent<LogoutWarningPrefDetail>).detail;
      if (typeof detail?.logoutWarningPref === 'boolean') {
        setDisableLogoutWarning(toDisableWarning(detail.logoutWarningPref));
      }
    };

    window.addEventListener(LOGOUT_WARNING_PREF_EVENT, handleLogoutWarningPrefChange);
    return () => {
      window.removeEventListener(LOGOUT_WARNING_PREF_EVENT, handleLogoutWarningPrefChange);
    };
  }, []);

  useEffect(() => {
    const termsRequired = globalContext?.loggedInEmployee?.user_terms_of_use?.required;
    if (!configLoaded || !globalContext || showFacilitySelector || !termsRequired) {
      setShowTermsOfUseModal(false);
      setTermsOfUseLoadError(null);
      setTermsOfUseLoading(false);
      return;
    }

    let ignore = false;
    setShowTermsOfUseModal(true);
    setTermsOfUseLoadError(null);
    setTermsOfUseMarkdown('');
    setTermsOfUseLoading(true);

    fetch('/auth/api/terms-of-use-status', {
      method: 'GET',
      credentials: 'include',
    })
      .then(async response => {
        if (!response.ok) {
          throw new Error('Unable to load terms of use');
        }
        return response.json();
      })
      .then(data => {
        if (ignore) {
          return;
        }

        if (!data.required || !data.markdown) {
          handleTermsAccepted(data.accepted_on || null, data.version || null);
          return;
        }

        setTermsOfUseMarkdown(data.markdown);
      })
      .catch(error => {
        if (ignore) {
          return;
        }
        console.error('Failed to load terms of use:', error);
        setTermsOfUseLoadError('Unable to load the Terms of Use. Please try again.');
      })
      .finally(() => {
        if (!ignore) {
          setTermsOfUseLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [configLoaded, globalContext, showFacilitySelector]);

  const initializeConfiguration = async () => {
    try {
      // Get global context from Django template for fallback
      const global = (window as any);
      const context: GlobalContext = {
        loggedInEmployee: global.loggedInEmployee,
        tenantContext: global.tenantContext,
        authContext: global.authContext,
        appContext: global.appContext
      };
      
      setGlobalContext(context);


      // Initialize tenant configuration from API
      const { initializeTenantConfig } = await import('../../config/tenant');
      const config = await initializeTenantConfig();
      
      setTenantConfig(config);
      setConfigLoaded(true);

      // Load facilities into Redux (shared across all pages)
      dispatch(fetchFacilities());

      // Check authentication status (skip for login page)
      const isLoginPage = location.pathname === '/login';
      if (!config.user.authenticated && !isLoginPage) {
        console.warn('User not authenticated, redirecting to login');
        // Redirect to login with return path (including hash for deep links)
        const returnPath = encodeURIComponent(location.pathname + location.search + location.hash);
        window.location.href = `/login?next=${returnPath}`;
        return;
      }

      // Check if facility selection is needed (matches AngularJS behavior)
      // Show facility selector if no facility is currently selected.
      //
      // This deliberately does NOT exempt quality_dashboard holders. It used to,
      // on the theory that the quality dashboard spans all facilities -- but that
      // suppressed facility selection for every OTHER screen those users touch
      // (encounters, patients), leaving TECdoc Admins with no facility at all.
      //
      // Since #243 the choice made here also seeds the Quality Dashboard, which used
      // to ignore it and always open on every active facility. See
      // utils/facilityScope.ts and get_facility_scope in TECWeb/TECCore/view_master.py.
      // An empty facility_ids list still means "every active facility" to
      // TECDashboard.views.get_quality_dashboard -- that is now what the explicit
      // "All facilities" choice sends, rather than what everyone gets by default.
      const currentFacilityId = context.tenantContext?.facilityId ||
                                global.facility?.id ||
                                global.loggedInEmployee?.current_facility_id;
      if (!currentFacilityId) {
        // Delay showing modal slightly to ensure UI is ready
        setTimeout(() => {
          setShowFacilitySelector(true);
        }, 100);
      }
      
    } catch (error) {
      console.error('Failed to initialize shell configuration:', error);
      setConfigError(error instanceof Error ? error.message : 'Unknown error');
    }
  };


  // Logout functionality will be added back when we rebuild the header

  // Facility switching - will be implemented in future issues
  // const handleFacilitySwitch = (facilityId: string) => {
  //   fetch('/facility', {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //       'X-CSRFToken': (globalContext?.authContext as any)?.csrf_token || ''
  //     },
  //     credentials: 'include',
  //     body: JSON.stringify({ id: facilityId })
  //   })
  //   .then(response => response.json())
  //   .then(data => {
  //     if (data.success) window.location.reload();
  //   })
  //   .catch(error => console.error('Facility switch error:', error));
  // };

  // Show error state if configuration fails
  if (configError) {
    return (
      <ErrorBoundary>
        <div className="shell-layout shell-layout--error">
          <header className="shell-header shell-header--error">
            <h1>TECDocChart Shell - Configuration Error</h1>
            <p>{configError}</p>
          </header>
          <main className="shell-main">
            <div className="error-details">
              <h2>Unable to Load Shell Configuration</h2>
              <button onClick={() => window.location.reload()}>
                Retry Initialization
              </button>
            </div>
          </main>
        </div>
      </ErrorBoundary>
    );
  }

  // Show loading state while initializing
  if (!configLoaded || !tenantConfig || !globalContext) {
    return (
      <ErrorBoundary>
        <div className="shell-layout shell-layout--loading">
          <main className="shell-main">
            <Loading message="" />
          </main>
        </div>
      </ErrorBoundary>
    );
  }

  const tenant = globalContext.tenantContext;

  const handleTermsAccepted = (acceptedOn: string | null, version: string | null) => {
    setGlobalContext(previous => {
      if (!previous?.loggedInEmployee?.user_terms_of_use) {
        return previous;
      }

      return {
        ...previous,
        loggedInEmployee: {
          ...previous.loggedInEmployee,
          user_terms_of_use: {
            ...previous.loggedInEmployee.user_terms_of_use,
            required: false,
            accepted_on: acceptedOn,
            version,
          },
        },
      };
    });
    setTermsOfUseMarkdown('');
    setShowTermsOfUseModal(false);
  };

  const handleTermsDeclined = () => {
    fetch('/api/auth/logout/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': globalContext?.authContext?.csrf_token || '',
      },
      credentials: 'include',
    })
      .finally(() => {
        window.location.href = '/login';
      });
  };

  const retryLoadTermsOfUse = async () => {
    setTermsOfUseLoading(true);
    setTermsOfUseLoadError(null);
    try {
      const response = await fetch('/auth/api/terms-of-use-status', {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Unable to load terms of use');
      }

      const data = await response.json();
      if (!data.required || !data.markdown) {
        handleTermsAccepted(data.accepted_on || null, data.version || null);
        return;
      }

      setTermsOfUseMarkdown(data.markdown);
    } catch (error) {
      console.error('Failed to reload terms of use:', error);
      setTermsOfUseLoadError('Unable to load the Terms of Use. Please try again.');
    } finally {
      setTermsOfUseLoading(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="shell-layout" data-tenant={tenant?.schema}>
        {/* Header matching master.html top-bar */}
        <header className="top-bar">
          <a href="/" className="top-bar-logo">
            <img 
              src={globalContext.appContext?.logoSmall?.replace(/&amp;/g, '&')} 
              alt="TECDoc Logo"
            />
            {globalContext.appContext?.singleUser ? (
              <h5>TECDoc Scribe</h5>
            ) : (
              <h5>TECDoc EMR</h5>
            )}
          </a>
          
          {/* Loading Throbber - matches original master.html position */}
          <TopBarThrobber visible={loading.isLoading} />
          
          {/* Top Bar Icons */}
          <ul className="top-bar-icons">
            {/* Facility Selector Icon */}
            <li 
              onClick={() => setShowFacilitySelector(true)}
              title="Select Facility"
              style={{
                cursor: 'pointer',
                width: '50px',
                height: '50px'
              }}
            >
              <TecSprite icon="building" color="black" size="md" />
            </li>
            
            {/* Slash Phrases Icon */}
            <li 
              onClick={() => setShowSlashPhrasesModal(true)}
              title="Update Slash Phrases"
              style={{
                cursor: 'pointer',
                border: 'gray 2px solid',
                padding: '2px',
                fontSize: 'medium',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '35px',
                height: '35px',
                backgroundColor: 'white',
                color: 'black'
              }}
            >
              \\&nbsp;
            </li>
            
            {/* Feedback Icon */}
            {globalContext.appContext?.feedbackEmail && globalContext.appContext.feedbackEmail.trim() && (
              <li 
                className="tec-sprite-black tec-sprite-mail-md"
                id="feedback-button"
                onClick={() => setShowFeedbackModal(true)}
                title="Send Feedback"
                style={{
                  cursor: 'pointer',
                  width: '50px',
                  height: '50px'
                }}
              />
            )}
          </ul>
          
          <div className="top-bar-user" onClick={(e) => {
            const dropdown = e.currentTarget.querySelector('.dropdown');
            if (dropdown) {
              dropdown.classList.toggle('hidden');
            }
          }}>
            <div className="tec-standin">
              <TecSprite icon="building" color="black" size="sm" />
            </div>
            <h3>
              {globalContext.authContext?.user?.username || 'User'}
            </h3>
            <i className="fa fa-chevron-down"></i>
            <div className="dropdown hidden">
              <a href="/account/"><h4>Account</h4></a>
              <hr />
              {/* Feedback lives in the menu, matching master_mobile.html's
                  Account / Send Feedback / Logout dropdown. The top-bar envelope
                  icon is hidden on phones, so this is the only way to reach it there. */}
              {globalContext.appContext?.feedbackEmail && globalContext.appContext.feedbackEmail.trim() && (
                <>
                  <a href="#" onClick={(e) => {
                    e.preventDefault();
                    setShowFeedbackModal(true);
                  }}><h4>Send Feedback</h4></a>
                  <hr />
                </>
              )}
              <a href="#" onClick={(e) => {
                e.preventDefault();
                // POST request to logout endpoint
                fetch('/api/auth/logout/', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': globalContext?.authContext?.csrf_token || ''
                  },
                  credentials: 'include'
                })
                .then(response => {
                  if (response.ok) {
                    // Redirect to login page after successful logout
                    window.location.href = '/login';
                  } else {
                    // Fallback: redirect to login anyway
                    window.location.href = '/login';
                  }
                })
                .catch(() => {
                  // Fallback: redirect to login anyway
                  window.location.href = '/login';
                });
              }}><h4>Logout</h4></a>
            </div>
          </div>
        </header>


        {/* Main Layout with Sidebar Navigation */}
        <div className="main-layout">
          {/* Left Sidebar Navigation - matching master.html main-navbar */}
          <MainNavbar globalContext={globalContext} />

          {/* Main Content Area */}
          <main className="shell-main">
            <Suspense fallback={<div className="loading-container"><div className="loading-spinner" /></div>}>
              <Outlet />
            </Suspense>
          </main>
        </div>

        {/* Feedback Modal */}
        {globalContext.appContext?.feedbackEmail && (
          <FeedbackModal
            isOpen={showFeedbackModal}
            onClose={() => setShowFeedbackModal(false)}
            feedbackEmail={globalContext.appContext.feedbackEmail}
            globalContext={globalContext}
          />
        )}

        {/* Slash Phrases Modal */}
        <SlashPhrasesModal
          isOpen={showSlashPhrasesModal}
          onClose={() => setShowSlashPhrasesModal(false)}
          globalContext={globalContext}
        />

        {/* Facility Login Selector Modal */}
        <FacilityLoginSelector
          isOpen={showFacilitySelector}
          onClose={() => setShowFacilitySelector(false)}
          onFacilitySelected={() => {}}
          globalContext={globalContext}
        />

        <TermsOfUseModal
          isOpen={showTermsOfUseModal}
          markdown={termsOfUseMarkdown}
          isLoading={termsOfUseLoading}
          loadError={termsOfUseLoadError}
          csrfToken={globalContext.authContext?.csrf_token}
          onRetryLoad={retryLoadTermsOfUse}
          onDeclined={handleTermsDeclined}
          onAccepted={handleTermsAccepted}
        />

        {/* Toast Notifications */}
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#4CAF50',
              color: 'white',
              width: '100vw',
              maxWidth: 'none',
              borderRadius: 0,
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '12px 20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              margin: 0,
            },
          }}
          containerStyle={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            width: '100%',
            zIndex: 99999,
          }}
        />

      </div>
    </ErrorBoundary>
  );
}

export default ShellLayout;
